import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const alertVariants = cva(
  `tw-relative tw-w-[calc(100%-var(--spacing)*8)] tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-sm tw-grid tw-items-center
   has-[>svg]:tw-grid-cols-[calc(var(--spacing)*5)_1fr] tw-grid-cols-[0_1fr] tw-gap-y-0.5 [&>svg]:tw-size-6 tw-border-solid
   [&>svg]:tw-translate-y-0.5 [&>svg]:tw-text-current has-[>svg]:tw-gap-x-3 [&:not(:last-child)]:tw-mb-3`,
  {
    variants: {
      variant: {
        default: 'tw-bg-card tw-text-card-foreground',
        error:
          `tw-border-destructive/60 tw-text-destructive tw-bg-destructive/5 tw-shadow-none [&>svg]:tw-text-current
          [&_*[data-slot=alert-description]]:tw-text-destructive/90`,
        info:
          `tw-border-sky-500/50 tw-text-sky-700 tw-bg-sky-50 [&>svg]:tw-text-sky-600
          [&_*[data-slot=alert-description]]:tw-text-sky-600`,
        success:
          `tw-border-emerald-500/50 tw-text-emerald-700 tw-bg-emerald-50 [&>svg]:tw-text-emerald-600
          [&_*[data-slot=alert-description]]:tw-text-emerald-600`,
        warning:
          `tw-border-amber-500/50 tw-text-amber-700 tw-bg-amber-50 [&>svg]:tw-text-amber-600
          [&_*[data-slot=alert-description]]:tw-text-amber-600`,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'tw-col-start-2 tw-text-lg tw-line-clamp-1 tw-min-h-4 tw-font-medium tw-tracking-tight',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'tw-text-muted-foreground tw-col-start-2 tw-gap-1 tw-text-base [&_p]:tw-leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
